import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/apiHelpers';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  History,
  User,
  Calendar,
  Edit,
  Plus,
  Trash2,
  Eye,
  RefreshCw,
  FileText,
  ArrowRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Action type icons and colors
const ACTION_CONFIG = {
  create: { icon: Plus, color: 'bg-green-100 text-green-700', label: 'Created' },
  update: { icon: Edit, color: 'bg-blue-100 text-blue-700', label: 'Updated' },
  delete: { icon: Trash2, color: 'bg-red-100 text-red-700', label: 'Deleted' },
  view: { icon: Eye, color: 'bg-gray-100 text-gray-600', label: 'Viewed' },
  login: { icon: User, color: 'bg-purple-100 text-purple-700', label: 'Login' },
  restore: { icon: RefreshCw, color: 'bg-yellow-100 text-yellow-700', label: 'Restored' },
  archive: { icon: FileText, color: 'bg-orange-100 text-orange-700', label: 'Archived' },
  upload: { icon: Plus, color: 'bg-teal-100 text-teal-700', label: 'Uploaded' },
  default: { icon: Edit, color: 'bg-gray-100 text-gray-600', label: 'Action' },
};

const getActionConfig = (action) => {
  // Check for partial matches
  const actionLower = action?.toLowerCase() || '';
  if (actionLower.includes('create')) return ACTION_CONFIG.create;
  if (actionLower.includes('update')) return ACTION_CONFIG.update;
  if (actionLower.includes('delete')) return ACTION_CONFIG.delete;
  if (actionLower.includes('view')) return ACTION_CONFIG.view;
  if (actionLower.includes('login')) return ACTION_CONFIG.login;
  if (actionLower.includes('restore')) return ACTION_CONFIG.restore;
  if (actionLower.includes('archive')) return ACTION_CONFIG.archive;
  if (actionLower.includes('upload')) return ACTION_CONFIG.upload;
  return ACTION_CONFIG[action] || ACTION_CONFIG.default;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const isUUID = (val) => typeof val === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);

const isISODate = (val) => typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(val);

const formatISODate = (val) => {
  try {
    return new Date(val).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  } catch { return val; }
};

const formatFieldName = (field) => {
  if (!field) return '';
  return field
    .replace(/_id$/, '')           // remove trailing _id
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Format value for display - make it more readable
const formatValue = (value) => {
  if (value === null || value === undefined) return 'None';
  if (value === '') return 'Empty';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (isISODate(String(value))) return formatISODate(String(value));
  if (Array.isArray(value)) {
    if (value.length === 0) return 'None';
    return value.map(v => formatFieldName(String(v))).join(', ');
  }
  if (typeof value === 'object') {
    // Try to make objects more readable
    const entries = Object.entries(value);
    if (entries.length === 0) return 'None';
    return entries.map(([k, v]) => `${formatFieldName(k)}: ${formatValue(v)}`).join('; ');
  }
  // Format status values
  const strValue = String(value);
  if (strValue.includes('_')) {
    return formatFieldName(strValue);
  }
  return strValue;
};

// Generate a human-readable summary of the action
const getActionSummary = (log) => {
  const { action, details } = log;
  
  if (action === 'status_change' && details?.old_status && details?.new_status) {
    return `Status changed from "${formatValue(details.old_status)}" to "${formatValue(details.new_status)}"`;
  }
  if (action === 'status_change' && details?.from && details?.to) {
    return `Status changed: ${formatValue(details.from)} → ${formatValue(details.to)}`;
  }
  if (action === 'reschedule' && details?.from_time && details?.to_time) {
    const fmt = (t) => { try { return new Date(t).toLocaleString('en-US', {month:'short',day:'numeric',year:'numeric',hour:'numeric',minute:'2-digit',hour12:true}); } catch { return t; } };
    return `Rescheduled from ${fmt(details.from_time)} to ${fmt(details.to_time)}`;
  }
  if (action === 'update' && details?.fields_updated) {
    return `Updated: ${details.fields_updated.map(f => formatFieldName(f)).join(', ')}`;
  }
  if (action === 'waitlist_self_updated' && details?.fields_updated) {
    return `Client self-updated: ${details.fields_updated.map(f => formatFieldName(f)).join(', ')}`;
  }
  if (action === 'create') {
    return 'Record created';
  }
  if (action === 'login') {
    return 'User logged in';
  }
  return null;
};

// Render change details in a readable format
const ChangeDetails = ({ details, action }) => {
  if (!details || Object.keys(details).length === 0) return null;
  
  // Filter out internal fields, null values, and UUID-only values
  const skipFields = ['_id', 'id', 'created_at', 'updated_at', 'organization_id'];
  
  // For status_change, we already show this in summary
  if (action === 'status_change') {
    skipFields.push('old_status', 'new_status');
  }
  // For updates with fields_updated, we show in summary
  if (action === 'update' || action === 'waitlist_self_updated') {
    skipFields.push('fields_updated');
  }
  
  const filteredDetails = Object.entries(details).filter(([key, value]) => {
    if (skipFields.includes(key)) return false;
    if (value === null || value === undefined || value === '' || value === 'None') return false;
    if (isUUID(String(value))) return false;  // hide raw UUIDs
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });
  
  if (filteredDetails.length === 0) return null;
  
  return (
    <div className="mt-2 pl-4 border-l-2 border-gray-200 space-y-1">
      {filteredDetails.map(([key, value]) => (
        <div key={key} className="text-xs">
          <span className="text-gray-500">{formatFieldName(key)}:</span>{' '}
          <span className="text-gray-700">{formatValue(value)}</span>
        </div>
      ))}
    </div>
  );
};

export const ChangeHistoryModal = ({
  open,
  onOpenChange,
  entityType,
  entityId,
  entityName,
}) => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChangeHistory = async () => {
      if (!open || !entityType || !entityId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await axios.get(`${API}/audit-logs/${entityType}/${entityId}`);
        setLogs(response.data || []);
      } catch (err) {
        console.error('Error fetching change history:', err);
        setError(getApiErrorMessage(err, 'Failed to load change history'));
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchChangeHistory();
  }, [open, entityType, entityId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-teal-600" />
            Change History
          </DialogTitle>
          <DialogDescription>
            {entityName ? `Activity log for: ${entityName}` : `${entityType} audit trail`}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto min-h-0 max-h-[55vh] pr-2">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <History className="h-12 w-12 mb-2 text-gray-300" />
              <p className="text-red-500">{error}</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <History className="h-12 w-12 mb-2 text-gray-300" />
              <p>No change history found</p>
              <p className="text-sm">Changes will appear here as they occur</p>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              {logs.map((log, index) => {
                const config = getActionConfig(log.action);
                const ActionIcon = config.icon;
                const actionSummary = getActionSummary(log);
                
                return (
                  <div 
                    key={log.id || index}
                    className="flex gap-3 p-3 rounded-lg border bg-white hover:bg-gray-50 transition-colors"
                  >
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${config.color}`}>
                      <ActionIcon className="h-4 w-4" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={config.color}>
                            {formatFieldName(log.action) || config.label}
                          </Badge>
                          <span className="text-sm font-medium text-gray-900">
                            {log.user_name || 'System'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {formatTimestamp(log.timestamp)}
                        </span>
                      </div>
                      
                      {/* Action Summary - human readable */}
                      {actionSummary && (
                        <p className="mt-1 text-sm text-gray-700">{actionSummary}</p>
                      )}
                      
                      {/* Additional Details */}
                      <ChangeDetails details={log.details} action={log.action} />
                      
                      {/* IP Address if available */}
                      {log.ip_address && (
                        <div className="mt-1 text-xs text-gray-400">
                          IP: {log.ip_address}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="pt-4 border-t flex justify-between items-center flex-shrink-0">
          <span className="text-xs text-gray-400">
            Showing {logs.length} entries
          </span>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeHistoryModal;
